import { startTransition, useEffect, useRef, useState } from 'react';
import { StorefrontApiError } from '../../api/client';

// One read of the shop, for the elements whose content belongs to the store rather than to the page.
// Every one of them wants the same four answers — nothing yet, here it is, the shop has no such thing,
// the read failed — and the difference between the third and the fourth is the difference between a
// merchant who knows what to fix and one staring at a gap.

/** Where a read stands. `missing` is the shop answering "no such record", which is not a failure. */
export type ReadStatus = 'idle' | 'loading' | 'ready' | 'missing' | 'failed';

/** A read in progress or done with: where it stands, and what it holds once it lands. */
export type StoreRead<T> = { status: ReadStatus; data: T | null };

/** Nothing asked, and a read on its way, each as one object: setting either again is no change and redraws nothing. */
const IDLE: StoreRead<never> = { status: 'idle', data: null };
const LOADING: StoreRead<never> = { status: 'loading', data: null };

/** How long one read may take before the element gives up rather than holding the page open. */
const TIMEOUT_MS = 8000;

// Says whether a failed read means the shop has no such record, as against the read itself failing.
const isMissing = (fault: unknown): boolean => fault instanceof StorefrontApiError && fault.status === 404;

/**
 * Reads one thing from the shop, again only when the element names a different one.
 *
 * The reading function is held in a ref rather than in the dependencies on purpose: every caller builds
 * a fresh closure on every render, so depending on it would re-fetch on every keystroke in the builder.
 * The key is what actually identifies the read, and the key is what the effect watches.
 */
export function useStoreRead<T>(key: string | null, read: (signal: AbortSignal) => Promise<T>): StoreRead<T> {
  const [state, setState] = useState<StoreRead<T>>(key === null ? IDLE : LOADING);
  const latest = useRef(read);
  latest.current = read;

  useEffect(() => {
    if (key === null) { setState(IDLE); return; }
    let cancelled = false;
    setState(LOADING);
    // What lands is drawn as a transition: it arrives after the page is up, and drawing it must never hold a tap.
    latest.current(AbortSignal.timeout(TIMEOUT_MS))
      .then((data) => { if (!cancelled) startTransition(() => setState({ status: 'ready', data })); })
      .catch((fault: unknown) => {
        if (!cancelled) setState({ status: isMissing(fault) ? 'missing' : 'failed', data: null });
      });
    return () => { cancelled = true; };
  }, [key]);

  return state;
}
